import { useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { useRouter, type Location, type Route } from '@atlassian/react-resource-router';
import type { State } from './types.tsx';
import { restoreFromSession, saveToSession, cleanUrl, isNewJourney } from './utils/index.tsx';

const getInitialStateFromSession = (host: string): State => {
	const isNew = isNewJourney(host);
	const { prevUrl, attributes } = restoreFromSession(isNew) || {};
	return {
		prevUrl,
		attributes: {
			steps: 0,
			prevPage: null,
			isPrevPageSpa: null,
			currentPage: null,
			isCurrentPageSpa: null,
			...attributes,
			isInitial: true,
			isNew,
		},
	};
};

type DebouncedChangeType = {
	navJourneyState: State | null;
	location: Location;
	route: Route | null;
	setNavJourneyState: (arg1: State) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	createAnalyticsEvent: (arg1: Record<any, any>) => UIAnalyticsEvent;
};

const debouncedChange = debounce(
	({
		navJourneyState,
		location,
		route,
		createAnalyticsEvent,
		setNavJourneyState,
	}: DebouncedChangeType) => {
		const { prevUrl, attributes } =
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			navJourneyState || getInitialStateFromSession(window.location.host);
		const currentUrl = cleanUrl(location.pathname, location.search);
		const currentPage = route?.name;

		if (route?.isRedirect === true) return;

		if (
			currentUrl === null ||
			currentPage == null ||
			prevUrl === currentUrl // refresh page
		) {
			return;
		}

		const newAttributes: Attributes = {
			...attributes,
			prevPage: attributes?.currentPage,
			isPrevPageSpa: attributes?.isCurrentPageSpa,
			currentPage,
			isCurrentPageSpa: __SPA__,
		};

		const analyticsEvent = createAnalyticsEvent({});
		fireOperationalAnalytics(analyticsEvent, 'NavigationJourney navigated', newAttributes);
		newAttributes.steps += 1;
		newAttributes.isInitial = false;
		newAttributes.isNew = false;

		const newState = { prevUrl: currentUrl, attributes: newAttributes };
		setNavJourneyState(newState);
		saveToSession(newState);
	},
	1000,
);

export const NavigationJourney = () => {
	const [navJourneyState, setNavJourneyState] = useState<unknown>(null);
	const [{ location, route }] = useRouter();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(
		() =>
			debouncedChange({
				// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'State | null'.
				navJourneyState,
				location,
				route,
				createAnalyticsEvent,
				setNavJourneyState,
			}),
		[location, navJourneyState, route, createAnalyticsEvent, setNavJourneyState],
	);
	return null;
};
